import { useState, useEffect } from "react";
import {
    Button,
    Card,
    CardContent,
    Container,
    Box,
    Typography,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { APP_IMAGES } from "../../constants/AppImages";
import { ROUTES, LocalStorageVariables, intl } from "dmsbasereact";
import { APP_COLORS } from "../../theme/appTheme";
import { getSSOStatus, initiateMicrosoftLogin } from "../../utils/ssoService";

export const SsoLoginPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [ssoEnabled, setSsoEnabled] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(true);

    useEffect(() => {
        const checkSSOStatus = async () => {
            const result = await getSSOStatus();
            if (result.data && result.data.sso_enabled) {
                setSsoEnabled(true);
            } else {
                setError(intl.get("SSONotConfigured").d("SSO is not configured on this system"));
            }
            setCheckingStatus(false);
        };

        checkSSOStatus();
    }, []);

    const handleSSOLogin = () => {
        setError("");
        setLoading(true);
        initiateMicrosoftLogin();

        // 2. Set up an event listener to receive the token/JSON data back
        const handleMicrosoftCallback = (event) => {
            // IMPORTANT: Verify the origin of the message for security
            if (event.origin !== window.location.origin) {
              console.error("Untrusted origin:", event.origin);
              setLoading(false);
              window.removeEventListener('message', handleMicrosoftCallback);
              return;
            }

            if (event.data) {
                let tokenData = JSON.parse(event.data);
                sessionStorage.setItem(LocalStorageVariables.PageToken, JSON.stringify(tokenData));
                sessionStorage.setItem("expirationSwalShow", "true");
                if (tokenData.refresh_token) {
                    const refreshToken = {
                        refreshToken: tokenData.refresh_token,
                        tokenUses: 0
                    };
                    sessionStorage.setItem("refresh", JSON.stringify(refreshToken));
                }

                if (tokenData.inactivity_timer) {
                    localStorage.setItem(
                        LocalStorageVariables.InactivityTimer,
                        tokenData.inactivity_timer
                    );
                }

                setLoading(false);
                window.removeEventListener('message', handleMicrosoftCallback);
                window.location.href = ROUTES.Home;
            }
            else {
              setError(intl.get("SSOAuthFailed").d("SSO authentication failed"));
            }
        };

        window.addEventListener('message', handleMicrosoftCallback, false);
    };

    if (checkingStatus) {
        return (
            <Container
                maxWidth="false"
                disableGutters
                sx={{
                    background: '#FFFFFF',
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container
            className="sso-login-page"
            maxWidth="false"
            disableGutters
            sx={{
                background: '#FFFFFF',
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: "450px",
                    px: 2,
                }}
            >
                <Box
                    sx={{
                        mb: 4,
                        textAlign: "center",
                    }}
                >
                    <Box
                        component="img"
                        src={APP_IMAGES.LOGO}
                        alt="Lena Logo"
                        sx={{
                            width: "180px",
                            height: "auto",
                            mb: 3,
                            filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.3))",
                        }}
                    />
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: APP_COLORS.neutral.gray600,
                            fontWeight: 500,
                        }}
                    >
                        {intl.get("LenaSystemSubtitle").d("Lenalidomide Management System")}
                    </Typography>
                </Box>

                <Card
                    sx={{
                        width: "100%",
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                        borderRadius: 3,
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                textAlign: "center",
                                mb: 3,
                                fontWeight: 600,
                                color: APP_COLORS.neutral.gray900,
                            }}
                        >
                            {intl.get("SSOLogin").d("SSO Login")}
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {loading && (
                            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        {ssoEnabled && !loading && (
                            <>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: "center",
                                        mb: 3,
                                        color: APP_COLORS.neutral.gray600,
                                    }}
                                >
                                    {intl.get("SSOLoginDescription").d("Sign in using your organization's Microsoft account")}
                                </Typography>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={<BusinessIcon />}
                                    onClick={handleSSOLogin}
                                    disabled={loading}
                                    sx={{
                                        backgroundColor: APP_COLORS.primary.main,
                                        color: APP_COLORS.primary.contrastText,
                                        height: "50px",
                                        textTransform: "none",
                                        fontSize: "16px",
                                        fontWeight: "bold",
                                        borderRadius: 2,
                                        mb: 2,
                                        "&:hover": {
                                            backgroundColor: APP_COLORS.primary.dark,
                                        },
                                    }}
                                >
                                    {intl.get("SignInWithMicrosoft").d("Sign in with Microsoft")}
                                </Button>
                            </>
                        )}

                        <Button
                            variant="text"
                            fullWidth
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(ROUTES.Login)}
                            sx={{
                                textTransform: "none",
                                color: APP_COLORS.primary.main,
                                mt: 1,
                            }}
                        >
                            {intl.get("BackToLogin").d("Back to Login")}
                        </Button>
                    </CardContent>
                </Card>

                <Box sx={{ mt: 4, textAlign: "center" }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: APP_COLORS.neutral.gray500,
                        }}
                    >
                        {intl.get("CopyrightLena", { year: new Date().getFullYear() }).d("© {year} Lena. All rights reserved.")}
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
};
